import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MappingExportEnvelope, SavedColumnMapping, isValidSavedColumnMapping } from '../../../models/bulk-payment.model';

const EXPECTED_MAPPING_TYPE = 'BULK_PAYMENT_COLUMN';

@Component({
  selector: 'app-import-mapping-modal',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './import-mapping-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImportMappingModalComponent {
  @Input() data: any;

  constructor(private cdr: ChangeDetectorRef) {}

  isDragOver = false;
  fileName: string | null = null;
  parsedMapping: SavedColumnMapping | null = null;
  previewJson: string = '';
  isValid = false;
  errorMessage: string | null = null;

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(): void {
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) this.readFile(file);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) this.readFile(file);
    input.value = '';
  }

  triggerFileInput(input: HTMLInputElement): void {
    input.click();
  }

  private readFile(file: File): void {
    this.fileName = file.name;
    this.parsedMapping = null;
    this.previewJson = '';
    this.isValid = false;
    this.errorMessage = null;
    this.cdr.markForCheck();

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      this.parseAndValidate(text);
      this.cdr.markForCheck();
    };
    reader.onerror = () => {
      this.errorMessage = 'Could not read the file.';
      this.cdr.markForCheck();
    };
    reader.readAsText(file);
  }

  private parseAndValidate(text: string): void {
    let envelope: any;
    try {
      envelope = JSON.parse(text);
    } catch {
      this.errorMessage = 'Invalid JSON file — could not parse.';
      return;
    }

    if (!envelope || typeof envelope !== 'object') {
      this.errorMessage = 'File does not contain a valid mapping envelope.';
      return;
    }

    if (envelope.mappingType !== EXPECTED_MAPPING_TYPE) {
      this.errorMessage = `Invalid mapping type "${envelope.mappingType ?? '(missing)'}". Expected "${EXPECTED_MAPPING_TYPE}".`;
      return;
    }

    if (!isValidSavedColumnMapping(envelope.mapping)) {
      this.errorMessage = 'The mapping payload does not match the expected column-mapping shape.';
      return;
    }

    const env = envelope as MappingExportEnvelope;
    this.parsedMapping = env.mapping;
    this.previewJson = JSON.stringify(env.mapping, null, 2);
    this.isValid = true;
    this.errorMessage = null;
  }
}
